import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, TrendingUp, TrendingDown, DollarSign, Users, Clock, 
  FileText, CheckCircle, ArrowUp, ArrowDown, Calendar, PieChart,
  Activity, Target, Briefcase, RefreshCw
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart as RechartsPieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#f97316', '#3b82f6', '#22c55e', '#eab308', '#8b5cf6', '#ec4899', '#14b8a6'];

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [customerInsights, setCustomerInsights] = useState(null);
  const [quotePerformance, setQuotePerformance] = useState(null);
  const [labourEfficiency, setLabourEfficiency] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAllAnalytics();
  }, []);

  const loadAllAnalytics = async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        trendRes,
        typesRes,
        customersRes,
        quotesRes,
        labourRes,
        monthlyRes
      ] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/analytics/revenue-trend`),
        axios.get(`${API}/analytics/project-types`),
        axios.get(`${API}/analytics/customer-insights`),
        axios.get(`${API}/analytics/quote-performance`),
        axios.get(`${API}/analytics/labour-efficiency`),
        axios.get(`${API}/analytics/monthly-summary`)
      ]);

      setOverview(overviewRes.data);
      setRevenueTrend(trendRes.data.trend);
      setProjectTypes(typesRes.data.breakdown);
      setCustomerInsights(customersRes.data);
      setQuotePerformance(quotesRes.data);
      setLabourEfficiency(labourRes.data);
      setMonthlySummary(monthlyRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "text-primary" }) => (
    <Card className="border-border hover:border-primary/30 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">{title}</p>
            <p className={`text-3xl font-bold font-mono ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                {trend === 'up' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                {trendValue}
              </div>
            )}
          </div>
          <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
            <Icon className="text-primary" size={24} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-primary" size={32} />
          <p className="text-muted-foreground font-mono uppercase tracking-wider">Loading Analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
            Analytics Dashboard
          </h1>
          <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
            Business Performance & Insights
          </p>
        </div>
        <Button onClick={loadAllAnalytics} variant="outline" className="font-mono uppercase text-xs">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-border pb-2 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'revenue', label: 'Revenue', icon: DollarSign },
          { id: 'quotes', label: 'Quotes', icon: FileText },
          { id: 'projects', label: 'Projects', icon: Briefcase },
          { id: 'customers', label: 'Customers', icon: Users },
          { id: 'labour', label: 'Labour', icon: Clock }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-mono uppercase tracking-wider rounded-sm transition-colors ${
              activeTab === tab.id 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Revenue"
              value={formatCurrency(overview.revenue.total)}
              subtitle={`${overview.period.days} days`}
              icon={DollarSign}
              color="text-green-500"
            />
            <StatCard
              title="Quotes Created"
              value={overview.quotes.total}
              subtitle={`${overview.quotes.conversion_rate}% converted`}
              icon={FileText}
              color="text-blue-500"
            />
            <StatCard
              title="Projects"
              value={overview.projects.total}
              subtitle={`${overview.projects.completed} completed`}
              icon={Briefcase}
              color="text-purple-500"
            />
            <StatCard
              title="Labour Hours"
              value={overview.labour.total_hours.toLocaleString()}
              subtitle={`${overview.labour.efficiency}% efficiency`}
              icon={Clock}
              color="text-orange-500"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
                  <TrendingUp size={20} />
                  Revenue Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#888" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#888" tickFormatter={(v) => `$${v/1000}k`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="#f97316" fill="#f97316" fillOpacity={0.3} name="Revenue" />
                    <Area type="monotone" dataKey="quote_value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} name="Quote Value" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Project Types */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
                  <PieChart size={20} />
                  Projects by Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={projectTypes.slice(0, 6)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="type"
                    >
                      {projectTypes.slice(0, 6).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Revenue Tab */}
      {activeTab === 'revenue' && monthlySummary && (
        <div className="space-y-6">
          {/* Monthly Revenue Chart */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold uppercase tracking-tight">
                Monthly Revenue - {monthlySummary.year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={monthlySummary.months}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="short_name" tick={{ fontSize: 12 }} stroke="#888" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#888" tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), name]}
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#22c55e" name="Revenue" />
                  <Bar dataKey="quotes_value" fill="#3b82f6" name="Quote Value" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Year Totals */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="Annual Revenue"
              value={formatCurrency(monthlySummary.totals.revenue)}
              icon={DollarSign}
              color="text-green-500"
            />
            <StatCard
              title="Total Quotes"
              value={monthlySummary.totals.quotes}
              subtitle={formatCurrency(monthlySummary.totals.quotes_value)}
              icon={FileText}
            />
            <StatCard
              title="Projects Started"
              value={monthlySummary.totals.projects}
              icon={Briefcase}
            />
            <StatCard
              title="Labour Hours"
              value={monthlySummary.totals.labour_hours.toLocaleString()}
              icon={Clock}
            />
          </div>
        </div>
      )}

      {/* Quotes Tab */}
      {activeTab === 'quotes' && quotePerformance && (
        <div className="space-y-6">
          {/* Conversion Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="Conversion Rate"
              value={`${quotePerformance.conversion.rate}%`}
              icon={Target}
              color="text-green-500"
            />
            <StatCard
              title="Converted"
              value={quotePerformance.conversion.converted}
              icon={CheckCircle}
              color="text-green-500"
            />
            <StatCard
              title="Pending"
              value={quotePerformance.conversion.pending}
              icon={Clock}
              color="text-yellow-500"
            />
            <StatCard
              title="Avg Quote Value"
              value={formatCurrency(quotePerformance.average_quote_value)}
              icon={DollarSign}
            />
          </div>

          {/* Conversion by Value Range */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold uppercase tracking-tight">
                Conversion by Quote Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={quotePerformance.by_value} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#888" />
                  <YAxis type="category" dataKey="range" tick={{ fontSize: 12 }} stroke="#888" width={120} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                  />
                  <Legend />
                  <Bar dataKey="count" fill="#3b82f6" name="Total Quotes" />
                  <Bar dataKey="converted" fill="#22c55e" name="Converted" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Projects Tab */}
      {activeTab === 'projects' && projectTypes && (
        <div className="space-y-6">
          {/* Project Types Table */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold uppercase tracking-tight">
                Projects by Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Type</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Count</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Value</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Completed</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Labour Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectTypes.map((type, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-3 px-4 font-medium">{type.type}</td>
                        <td className="text-right py-3 px-4 font-mono">{type.count}</td>
                        <td className="text-right py-3 px-4 font-mono text-green-500">{formatCurrency(type.total_value)}</td>
                        <td className="text-right py-3 px-4">
                          <Badge variant={type.completed === type.count ? "default" : "secondary"}>
                            {type.completed}/{type.count}
                          </Badge>
                        </td>
                        <td className="text-right py-3 px-4 font-mono">{type.labour_hours.toFixed(1)}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && customerInsights && (
        <div className="space-y-6">
          {/* Customer Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              title="Total Customers"
              value={customerInsights.summary.total_customers}
              icon={Users}
            />
            <StatCard
              title="Repeat Customers"
              value={customerInsights.summary.repeat_customers}
              subtitle={`${customerInsights.summary.repeat_rate}% repeat rate`}
              icon={Users}
              color="text-green-500"
            />
            <StatCard
              title="Top Customer Value"
              value={customerInsights.customers[0] ? formatCurrency(customerInsights.customers[0].total_value) : '$0'}
              subtitle={customerInsights.customers[0]?.name || 'N/A'}
              icon={DollarSign}
            />
          </div>

          {/* Top Customers Table */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold uppercase tracking-tight">
                Top Customers by Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Customer</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Projects</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Total Value</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Completed</th>
                      <th className="text-center py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerInsights.customers.map((customer, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-3 px-4">
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-xs text-muted-foreground">{customer.email}</p>
                        </td>
                        <td className="text-right py-3 px-4 font-mono">{customer.project_count}</td>
                        <td className="text-right py-3 px-4 font-mono text-green-500">{formatCurrency(customer.total_value)}</td>
                        <td className="text-right py-3 px-4 font-mono">{customer.completed}</td>
                        <td className="text-center py-3 px-4">
                          {customer.repeat_customer ? (
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Repeat</Badge>
                          ) : (
                            <Badge variant="secondary">New</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Labour Tab */}
      {activeTab === 'labour' && labourEfficiency && (
        <div className="space-y-6">
          {/* Labour Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="Efficiency Rate"
              value={`${labourEfficiency.efficiency_rate}%`}
              icon={Activity}
              color={labourEfficiency.efficiency_rate >= 90 ? "text-green-500" : labourEfficiency.efficiency_rate >= 70 ? "text-yellow-500" : "text-red-500"}
            />
            <StatCard
              title="Total Estimated"
              value={`${labourEfficiency.total_estimated_hours.toFixed(1)}h`}
              icon={Clock}
            />
            <StatCard
              title="Total Actual"
              value={`${labourEfficiency.total_actual_hours.toFixed(1)}h`}
              icon={Clock}
            />
            <StatCard
              title="On Budget"
              value={labourEfficiency.on_budget_projects}
              subtitle={`${labourEfficiency.over_budget_projects} over, ${labourEfficiency.under_budget_projects} under`}
              icon={Target}
              color="text-green-500"
            />
          </div>

          {/* Project Efficiency Table */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold uppercase tracking-tight">
                Project Labour Variance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Project</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Estimated</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Actual</th>
                      <th className="text-right py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Variance</th>
                      <th className="text-center py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {labourEfficiency.projects.map((project, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-3 px-4">
                          <p className="font-medium">{project.project_name}</p>
                          <p className="text-xs text-muted-foreground">{project.client_name}</p>
                        </td>
                        <td className="text-right py-3 px-4 font-mono">{project.estimated_hours.toFixed(1)}h</td>
                        <td className="text-right py-3 px-4 font-mono">{project.actual_hours.toFixed(1)}h</td>
                        <td className={`text-right py-3 px-4 font-mono ${
                          project.variance_percent > 10 ? 'text-red-500' : 
                          project.variance_percent < -10 ? 'text-green-500' : 
                          'text-yellow-500'
                        }`}>
                          {project.variance_percent > 0 ? '+' : ''}{project.variance_percent}%
                        </td>
                        <td className="text-center py-3 px-4">
                          <Badge 
                            className={
                              project.status === 'over' ? 'bg-red-500/10 text-red-500 border-red-500/20' :
                              project.status === 'under' ? 'bg-green-500/10 text-green-500 border-green-500/20' :
                              'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                            }
                          >
                            {project.status === 'over' ? 'Over Budget' : 
                             project.status === 'under' ? 'Under Budget' : 
                             'On Budget'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
